// Typed client for the FastAPI /api/v1 layer. Unwraps the {success,data,meta} envelope and turns
// {success:false,error} into a thrown ApiError. Cookies are same-origin (Vite proxies /api).

const BASE = "/api/v1";

export class ApiError extends Error {
  code: string;
  status: number;
  constructor(code: string, message: string, status: number) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

export interface PageMeta {
  page: number;
  page_size: number;
  total: number;
  pages: number;
}

export interface Envelope<T> {
  data: T;
  meta?: PageMeta;
}

type Query = Record<string, string | number | boolean | null | undefined>;

function qs(params?: Query): string {
  if (!params) return "";
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== null && v !== undefined && v !== "") sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

async function request<T>(
  method: string,
  path: string,
  opts: { query?: Query; body?: unknown } = {},
): Promise<Envelope<T>> {
  const res = await fetch(`${BASE}${path}${qs(opts.query)}`, {
    method,
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
  });

  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    /* empty / non-json body */
  }

  const payload = json as { success?: boolean; data?: T; meta?: PageMeta; error?: { code: string; message: string } };

  if (!res.ok || payload?.success === false) {
    const err = payload?.error;
    if (res.status === 401 && !location.pathname.startsWith("/login")) {
      const back = encodeURIComponent(location.pathname + location.search);
      location.assign(`/login?next=${back}`);
    }
    throw new ApiError(err?.code ?? "HTTP_ERROR", err?.message ?? `HTTP ${res.status}`, res.status);
  }

  return { data: payload.data as T, meta: payload.meta };
}

export const api = {
  get: <T>(path: string, query?: Query) => request<T>("GET", path, { query }),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, { body }),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, { body }),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, { body }),
  delete: <T>(path: string) => request<T>("DELETE", path),
};

/** Stream an .xlsx export to the browser's downloads. `resource` is a list-resource name;
 *  `params` are the same filters as the list endpoint. Throws ApiError on failure. */
export async function downloadExport(resource: string, params?: Query): Promise<void> {
  const res = await fetch(`${BASE}/export/${resource}${qs(params)}`, { credentials: "same-origin" });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const j = (await res.json()) as { error?: { message?: string } };
      if (j.error?.message) msg = j.error.message;
    } catch {
      /* non-json */
    }
    throw new ApiError("EXPORT_FAILED", msg, res.status);
  }
  const blob = await res.blob();
  const cd = res.headers.get("content-disposition") ?? "";
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(cd)?.[1];
  const plain = /filename="([^"]+)"/.exec(cd)?.[1];
  let name = `${resource}.xlsx`;
  try {
    name = utf8 ? decodeURIComponent(utf8) : (plain ?? name);
  } catch {
    name = plain ?? name;
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
